import { DATE_FORMAT } from "../constants/app-constant";
import {
  EASTERN_TIME_ZONE,
  OPCD_API_STATUS,
  OPCD_RESPONSE_STATUS,
} from "../constants/opcd-constants";
import type {
  OpcdQueriesListResponse,
  OpcdQuery,
  OpcdQueryListRow,
  OpcdQueryOption,
  OpcdQueryResponse,
} from "../types/opcd-queries";
import { calculateAging, formatDate, formatTimeWithZoneAbbreviation } from ".";

const normalizeStatus = (status?: string): string =>
  (status ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");

export const isOpcdQueryPending = (status?: string): boolean =>
  normalizeStatus(status) === normalizeStatus(OPCD_API_STATUS.PENDING);

export const isOpcdQueryCompleted = (
  status?: string,
  response?: OpcdQueryResponse | null
): boolean => {
  const normalized = normalizeStatus(status);
  return (
    normalized === normalizeStatus(OPCD_API_STATUS.COMPLETED) ||
    normalized === "provider_responded" ||
    normalized === "responded" ||
    normalizeStatus(response?.status) === OPCD_RESPONSE_STATUS.COMPLETED
  );
};

export const extractOpcdQueriesListResponse = (
  payload: unknown
): OpcdQueriesListResponse => {
  const empty: OpcdQueriesListResponse = {
    success: false,
    count: 0,
    queries: [],
  };
  if (!payload || typeof payload !== "object") return empty;

  const root = payload as Record<string, unknown>;
  if (Array.isArray(root.queries)) {
    return {
      success: Boolean(root.success),
      count: Number(root.count ?? root.queries.length),
      queries: root.queries as OpcdQuery[],
    };
  }

  if (root.data && typeof root.data === "object") {
    return extractOpcdQueriesListResponse(root.data);
  }

  return empty;
};

export const extractOpcdQueryDetail = (payload: unknown): OpcdQuery | null => {
  if (!payload || typeof payload !== "object") return null;
  const root = payload as Record<string, unknown>;

  if (root.query && typeof root.query === "object") {
    return root.query as OpcdQuery;
  }
  if (root.data && typeof root.data === "object") {
    return extractOpcdQueryDetail(root.data);
  }
  return null;
};

export const transformOpcdQueriesToRows = (
  response: OpcdQueriesListResponse
): OpcdQueryListRow[] =>
  (response.queries ?? []).map((query) => {
    const dateSource = query.sentAt || query.createdAt;
    const completed = isOpcdQueryCompleted(query.status, query.response);
    return {
      id: query.queryId,
      queryId: query.queryId,
      patientName: query.request?.patient?.name || "-",
      patientMRN: query.request?.patient?.mrn || "-",
      status: completed
        ? OPCD_API_STATUS.COMPLETED
        : isOpcdQueryPending(query.status)
        ? "Open"
        : query.status || "-",
      statusApi: completed ? OPCD_API_STATUS.COMPLETED : query.status,
      ageing: completed ? "-" : calculateAging(dateSource),
      queryDate: formatDate(
        dateSource,
        DATE_FORMAT.MM_DD_YYYY,
        EASTERN_TIME_ZONE
      ),
      createdFormatTime: formatTimeWithZoneAbbreviation(
        dateSource,
        EASTERN_TIME_ZONE,
        "ET"
      ),
    };
  });

export const filterOpcdQueriesByStatus = (
  rows: OpcdQueryListRow[],
  statuses: string[]
): OpcdQueryListRow[] => {
  if (!statuses.length || statuses.length === OPCD_STATUS_VALUES.length) {
    return rows;
  }
  const normalized = statuses.map(normalizeStatus);
  return rows.filter((row) => normalized.includes(normalizeStatus(row.statusApi)));
};

const OPCD_STATUS_VALUES = [
  OPCD_API_STATUS.PENDING,
  OPCD_API_STATUS.COMPLETED,
];

export const filterOpcdQueriesBySearch = (
  rows: OpcdQueryListRow[],
  search: string
): OpcdQueryListRow[] => {
  const term = search.trim().toLowerCase();
  if (!term) return rows;
  return rows.filter(
    (row) =>
      row.patientName.toLowerCase().includes(term) ||
      row.patientMRN.toLowerCase().includes(term) ||
      row.queryId.toLowerCase().includes(term)
  );
};

export const isOpcdOtherOption = (option: OpcdQueryOption): boolean =>
  /\bother\b/i.test(option.label.trim());

export const extractOpcdResponseSelections = (
  response?: OpcdQueryResponse | null
): { selectedIds: string[]; otherText: string } => ({
  selectedIds: (response?.selectedOptions ?? []).map((option) =>
    String(option.id)
  ),
  otherText: response?.otherJustification ?? "",
});

export const buildOpcdSubmitOptions = (
  options: OpcdQueryOption[],
  selectedIds: string[],
  otherText: string
): OpcdQueryOption[] =>
  selectedIds.map((id) => {
    const option = options.find((item) => String(item.id) === id);
    if (!option) return { id, label: id };
    if (isOpcdOtherOption(option) && otherText.trim()) {
      return { ...option, label: `${option.label}: ${otherText.trim()}` };
    }
    return option;
  });
